import { flexRender, getCoreRowModel, getSortedRowModel, type RowData, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo } from 'react';

import { Skeleton } from './Skeleton';
import type { TableColumnHeaderProps, TableListProps, TableRowProps } from './TableList.types';

const SkeletonRow = ({ columnCount }: { columnCount: number }) => (
  <tr className="border-border border-b">
    {Array.from({ length: columnCount }).map((_, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: Acceptable for static skeleton cells
      <td key={index} className="p-3 text-center">
        <Skeleton className="h-3.5 w-full rounded-sm" />
      </td>
    ))}
  </tr>
);

const EmptyStateRow = ({ columnCount }: { columnCount: number }) => (
  <tr>
    <td className="p-4 text-center" colSpan={columnCount}>
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <h3 className="mb-4 font-bold text-foreground text-lg">No results found</h3>
        <p className="text-balance text-base text-muted-foreground">
          There is no data available at the moment. Please check back later.
        </p>
      </div>
    </td>
  </tr>
);

const TableColumnHeader = <T,>({ header }: TableColumnHeaderProps<T>) => {
  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const sortHandler = header.column.getToggleSortingHandler();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (!canSort || !sortHandler) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      sortHandler(event);
    }
  };

  return (
    <th
      aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}
      className={clsx('border-0 border-border not-last:border-e p-3', {
        'cursor-pointer select-none transition-colors duration-200 hover:bg-accent focus-visible:outline-2 focus-visible:outline-primary':
          canSort,
        'text-primary': sortDirection !== false,
      })}
      colSpan={header.colSpan}
      onClick={sortHandler}
      onKeyDown={handleKeyDown}
      role={canSort ? 'columnheader button' : undefined}
      tabIndex={canSort ? 0 : undefined}
      style={{
        maxWidth: header.column.columnDef.maxSize,
        minWidth: header.column.columnDef.minSize,
        width: header.getSize(),
      }}
    >
      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
    </th>
  );
};

const TableRow = <T,>({ row, isClickable, onRowClick, className }: TableRowProps<T>) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row.original);
    }
  };

  return (
    <tr
      className={clsx('border-border border-b', className, {
        'transition duration-300 hover:cursor-pointer hover:bg-accent hover:shadow-lg': isClickable,
        'focus-visible:bg-accent focus-visible:shadow-lg focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2':
          isClickable,
      })}
      onClick={isClickable ? () => onRowClick?.(row.original) : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'row button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className="text-center text-sm"
          style={{
            width: cell.column.getSize(),
            minWidth: cell.column.columnDef.minSize,
            maxWidth: cell.column.columnDef.maxSize,
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
};

const TableList = <T extends RowData>({
  data,
  columns,
  sortingState = [],
  isLoading = false,
  skeletonRowCount = 3,
  rowClassName,
  onSortingChange,
  onRowClick,
}: TableListProps<T>) => {
  const table = useReactTable({
    data,
    columns,
    state: { sorting: sortingState },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: onSortingChange,
    enableSortingRemoval: false,
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getAllLeafColumns().length;
  const showEmpty = !isLoading && rows.length === 0;
  const isClickable = typeof onRowClick !== 'undefined';

  const skeletonRows = useMemo(
    () =>
      Array.from({ length: skeletonRowCount }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Acceptable for static skeleton rows
        <SkeletonRow key={index} columnCount={columnCount} />
      )),
    [columnCount, skeletonRowCount],
  );

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full bg-background">
        <thead className="border-border bg-muted">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className="border-border border-b" key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableColumnHeader key={header.id} header={header} />
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="[&_tr:last-child]:border-b-0">
          {isLoading ? (
            skeletonRows
          ) : showEmpty ? (
            <EmptyStateRow columnCount={columnCount} />
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                row={row}
                isClickable={isClickable}
                onRowClick={onRowClick}
                className={rowClassName?.(row.original)}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export { TableList };
